import ace, { Ace } from 'ace-builds';
import { useEffect, useMemo, useRef } from 'react';
import { useEventCallback } from '../../../utils/hooks/useEventCallback';
import { findIdAnnotations, type IdAnnotation } from '../lib/findIdAnnotations';

type SessionWithWidgets = Ace.EditSession & {
  widgetManager?: Ace.LineWidgets;
};

type UseIdAnnotationsOptions = {
  onIdClick?: (id: string) => void;
};

type WidgetWithAnnotation = {
  widget: Ace.LineWidget;
  annotationRow: number;
};

export const useIdAnnotations = (
  editor: Ace.Editor | null,
  jsonText: string,
  options?: UseIdAnnotationsOptions
): IdAnnotation[] => {
  const widgetsRef = useRef<WidgetWithAnnotation[]>([]);
  const onIdClickRef = useEventCallback(options?.onIdClick);

  const annotations = useMemo(() => findIdAnnotations(jsonText), [jsonText]);

  useEffect(() => {
    if (!editor) return undefined;

    const session = editor.getSession() as SessionWithWidgets;

    if (!session.widgetManager) {
      const { LineWidgets } = ace.require('ace/line_widgets') as {
        LineWidgets: new (session: Ace.EditSession) => Ace.LineWidgets;
      };
      const widgetManager = new LineWidgets(session);
      widgetManager.attach(editor);
      session.widgetManager = widgetManager;
    }

    const { widgetManager } = session;

    const currentWidgets: WidgetWithAnnotation[] = [];

    const updateVisibility = () => {
      const folds = session.getAllFolds();
      const foldedRows = new Set<number>();
      folds.forEach(fold => {
        foldedRows.add(fold.start.row);
      });

      currentWidgets.forEach(({ widget, annotationRow }) => {
        const shouldHide = foldedRows.has(annotationRow - 1);
        const { el } = widget;

        if (el) {
          el.style.display = shouldHide ? 'none' : '';
        }
      });
    };

    widgetsRef.current.forEach(({ widget }) => {
      widgetManager.removeLineWidget(widget);
    });
    widgetsRef.current = [];

    const charWidth = editor.renderer.characterWidth;
    const { lineHeight } = editor.renderer;

    annotations
      .filter(annotation => annotation.row > 0)
      .forEach(annotation => {
        const label = `# ${annotation.id}`;

        const el = document.createElement('div');
        el.className = 'schema-id-annotation';
        el.style.paddingLeft = `${5 + annotation.column * charWidth}px`;
        el.style.height = `${lineHeight}px`;
        el.style.lineHeight = `${lineHeight}px`;
        el.textContent = label;

        el.style.cursor = 'pointer';
        el.style.pointerEvents = 'auto';
        el.addEventListener('click', () => {
          onIdClickRef?.(annotation.id);
        });

        const widget: Ace.LineWidget = {
          row: annotation.row - 1,
          el,
          coverGutter: false,
          pixelHeight: lineHeight,
        };

        widgetManager.addLineWidget(widget);
        currentWidgets.push({ widget, annotationRow: annotation.row });
      });

    widgetsRef.current = currentWidgets;
    updateVisibility();

    session.on('changeFold', updateVisibility);

    return () => {
      session.off('changeFold', updateVisibility);
      currentWidgets.forEach(({ widget }) => {
        widgetManager.removeLineWidget(widget);
      });
      widgetsRef.current = [];
    };
  }, [editor, annotations, onIdClickRef]);

  return annotations;
};
